import React, { createContext, useReducer } from 'react';
import { User } from './__generated__/graphql';
import client from './apollo';
import api from './common/api';
import { LOCAL_STORAGE } from './common/constants';
import {
  AppAction,
  AppActionType,
  AppContextType,
  AppState,
} from './types/appContext.type';
import { ChildrenPropType } from './types/common.type';

const getLoggedInUser = (): User | null => {
  const loggedInUser = localStorage.getItem(LOCAL_STORAGE.USER);
  return loggedInUser ? JSON.parse(loggedInUser) : null;
};

const getStoredFloorPlanSequence = () => {
  const data = localStorage.getItem(LOCAL_STORAGE.UPLOAD_FLOOR_PLAN_SEQUENCE);
  return data ? JSON.parse(data) : null;
};

const getStoredFloorPlan = () => {
  const data = localStorage.getItem(LOCAL_STORAGE.FLOOR_PLAN);
  return data ? JSON.parse(data) : null;
};

const initialState: AppState = {
  currentUser: getLoggedInUser() || {},
  authenticated: !!localStorage.getItem(LOCAL_STORAGE.TOKEN),
  authToken: localStorage.getItem(LOCAL_STORAGE.TOKEN),
  setTourGuide: JSON.parse(
    localStorage.getItem(LOCAL_STORAGE.TOUR_GUIDE) || 'false',
  ),
  floorPlanSequence: getStoredFloorPlanSequence(),
  floorPlan: getStoredFloorPlan(),
  routerPrompt: false,
};

const reducer = (state: AppState, action: AppAction) => {
  switch (action?.type) {
    case AppActionType.setCurrentUser:
      // eslint-disable-next-line no-case-declarations
      const user = action.data || {};
      localStorage.setItem(
        LOCAL_STORAGE.USER,
        user && Object?.keys(user)?.length ? JSON?.stringify(user) : '',
      );
      return { ...state, currentUser: { ...user } };
    case AppActionType.setCurrentRole:
      return { ...state, currentRole: action.data };
    case AppActionType.logout:
      delete api?.defaults?.headers?.common?.Authorization;
      localStorage.clear();
      client?.clearStore();
      return {
        ...initialState,
        authenticated: false,
        authToken: null,
        user: {},
      };
    case AppActionType.setAuthenticated:
      return { ...state, authenticated: action.data };
    case AppActionType.setAuthToken:
      localStorage.setItem(LOCAL_STORAGE.TOKEN, action.data ?? '');
      return { ...state, authToken: action.data };
    case AppActionType.setTourGuide:
      localStorage.setItem(
        LOCAL_STORAGE.TOUR_GUIDE,
        JSON.stringify(action.data),
      );
      return { ...state, tourGuideStatus: action.data };
    case AppActionType.setFloorPlanSequence:
      localStorage.setItem(
        LOCAL_STORAGE.UPLOAD_FLOOR_PLAN_SEQUENCE,
        JSON.stringify(action.data),
      );
      return { ...state, floorPlanSequence: action.data };
    case AppActionType.getFloorPlanSequence:
      return {
        ...state,
        floorPlanSequence: getStoredFloorPlanSequence(),
      };
    case AppActionType.removeFloorPlanSequence:
      localStorage.removeItem(LOCAL_STORAGE.UPLOAD_FLOOR_PLAN_SEQUENCE);
      return { ...state, floorPlanSequence: null };
    case AppActionType.setFloorPlan:
      localStorage.setItem(
        LOCAL_STORAGE.FLOOR_PLAN,
        JSON.stringify(action.data),
      );
      return { ...state, floorPlan: action.data };
    case AppActionType.getFloorPlan:
      return { ...state, floorPlan: getStoredFloorPlan() };
    case AppActionType.setRefreshToken:
      localStorage.setItem(LOCAL_STORAGE.REFRESH_TOKEN, action.data ?? '');
      return {
        ...state,
        refreshToken: action.data,
      };
    case AppActionType.setRouterPrompt:
      return {
        ...state,
        routerPrompt: action.data,
      };
    default:
      return { ...state };
  }
};

const AppContext = createContext<AppContextType | null>(null);

const AppContextProvider: React.FC<ChildrenPropType> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const getToken = () => localStorage.getItem(LOCAL_STORAGE.TOKEN) || null;
  const getRefreshToken = () =>
    localStorage.getItem(LOCAL_STORAGE.REFRESH_TOKEN);
  const getCurrentUser = () =>
    localStorage?.getItem(LOCAL_STORAGE.USER)
      ? JSON?.parse(localStorage.getItem(LOCAL_STORAGE.USER) ?? '')
      : {};

  const isAuthenticated = () => state.authenticated;

  const initializeAuth = (
    authToken: string | null,
    userData: User | null,
    refreshToken?: string | null,
  ) => {
    const token = authToken || getToken();
    const user = userData || getCurrentUser();
    const refresh = refreshToken || getRefreshToken();
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      dispatch({ type: AppActionType.setAuthToken, data: token });
      dispatch({ type: AppActionType.setRefreshToken, data: refresh });
      dispatch({ type: AppActionType.setAuthenticated, data: true });
      dispatch({ type: AppActionType.setCurrentUser, data: user });
    }
  };

  const value: AppContextType = {
    state,
    dispatch,
    isAuthenticated,
    getToken,
    getRefreshToken,
    initializeAuth,
    getCurrentUser,
    getStoredFloorPlanSequence,
    getStoredFloorPlan,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

const AppContextConsumer = AppContext.Consumer;

export { AppContext, AppContextConsumer, AppContextProvider };
